import { unsetRating } from './access';
import type { IdGen } from './ids';
import type { Category, KinklistDoc, Rating } from './types';

/** Default legend, chosen upstream (kinklist-v2) to be colorblind-distinguishable. */
export const DEFAULT_RATINGS: readonly (readonly [name: string, color: string])[] = [
  ['Not Entered', '#FFFFFF'],
  ['Favorite', '#6DB5FE'],
  ['Like', '#23FD22'],
  ['Okay', '#FDFD6B'],
  ['Maybe', '#DB6C00'],
  ['No', '#920000'],
];

interface CategoryTemplate {
  name: string;
  roles: readonly string[];
  kinks: readonly string[];
}

/** Default content, verbatim from kinklist-v2 (SPEC Appendix A). */
export const DEFAULT_CATEGORIES: readonly CategoryTemplate[] = [
  {
    name: 'Bodies',
    roles: ['Self', 'Partner'],
    kinks: ['Skinny', 'Chubby', 'Small breasts', 'Large breasts', 'Small cocks', 'Large cocks'],
  },
  {
    name: 'Clothing',
    roles: ['Self', 'Partner'],
    kinks: [
      'Clothed sex',
      'Lingerie',
      'Stockings',
      'Heels',
      'Leather',
      'Latex',
      'Uniform / costume',
      'Cross-dressing',
    ],
  },
  {
    name: 'Groupings',
    roles: ['General'],
    kinks: [
      'You and 1 male',
      'You and 1 female',
      'You and trans female',
      'You and trans male',
      'You and 1 male, 1 female',
      'You and 2 males',
      'You and 2 females',
      'Orgy',
    ],
  },
  {
    name: 'General',
    roles: ['Giving', 'Receiving'],
    kinks: [
      'Romance / Affection',
      'Handjob / fingering',
      'Blowjob',
      'Deep throat',
      'Swallowing',
      'Facials',
      'Cunnilingus',
      'Face-sitting',
      'Edging',
      'Teasing',
      'JOI, SI',
    ],
  },
  {
    name: 'Ass play',
    roles: ['Giving', 'Receiving'],
    kinks: ['Anal toys', 'Anal sex, pegging', 'Rimming', 'Double penetration', 'Anal fisting'],
  },
  {
    name: 'Restrictive',
    roles: ['Self', 'Partner'],
    kinks: [
      'Gag',
      'Collar',
      'Leash',
      'Chastity',
      'Bondage (Light)',
      'Bondage (Heavy)',
      'Encasement',
    ],
  },
  {
    name: 'Toys',
    roles: ['Self', 'Partner'],
    kinks: ['Dildos', 'Plugs', 'Vibrators', 'Sounding'],
  },
  {
    name: 'Domination',
    roles: ['Dominant', 'Submissive'],
    kinks: [
      'Dominant / Submissive',
      'Domestic servitude',
      'Slavery',
      'Pet play',
      'DD/lg, MD/lb',
      'Discipline',
      'Begging',
      'Forced orgasm',
      'Orgasm control',
      'Orgasm denial',
      'Power exchange',
    ],
  },
  {
    name: 'No consent',
    roles: ['Aggressor', 'Target'],
    kinks: [
      'Non-con / rape',
      'Blackmail / coercion',
      'Kidnapping',
      'Drugs',
      'Alcohol',
      'Sleep play',
    ],
  },
  {
    name: 'Taboo',
    roles: ['General'],
    kinks: [
      'Incest',
      'Age gap',
      'Raceplay',
      'Bestiality',
      'Necrophilia',
      'Cheating',
      'Exhibitionism',
      'Voyeurism',
    ],
  },
  {
    name: 'Surrealism',
    roles: ['Self', 'Partner'],
    kinks: ['Futanari', 'Furry', 'Vore', 'Transformation', 'Tentacles', 'Monster or Alien'],
  },
  {
    name: 'Fluids',
    roles: ['General'],
    kinks: ['Blood', 'Watersports', 'Scat', 'Lactation', 'Diapers', 'Cum play'],
  },
  {
    name: 'Degradation',
    roles: ['Giving', 'Receiving'],
    kinks: ['Glory hole', 'Name calling', 'Humiliation'],
  },
  {
    name: 'Touch & Stimulation',
    roles: ['Actor', 'Subject'],
    kinks: [
      'Cock/Pussy worship',
      'Ass worship',
      'Foot play',
      'Tickling',
      'Sensation play',
      'Electro stimulation',
    ],
  },
  {
    name: 'Misc. Fetish',
    roles: ['Giving', 'Receiving'],
    kinks: [
      'Fisting',
      'Gangbang',
      'Breath play',
      'Impregnation',
      'Pregnancy',
      'Feminization',
      'Cuckold / Cuckquean',
    ],
  },
  {
    name: 'Pain',
    roles: ['Giving', 'Receiving'],
    kinks: [
      'Light pain',
      'Heavy pain',
      'Nipple clamps',
      'Clothes pins',
      'Caning',
      'Flogging',
      'Beating',
      'Spanking',
      'Cock/Pussy slapping',
      'Cock/Pussy torture',
      'Hot Wax',
      'Scratching',
      'Biting',
      'Cutting',
    ],
  },
];

export function createDefaultRatings(newId: IdGen): Rating[] {
  return DEFAULT_RATINGS.map(([name, color]) => ({ id: newId(), name, color }));
}

/** Builds a category whose kinks all start at `unsetId`. */
export function createCategory(
  template: CategoryTemplate,
  unsetId: string,
  newId: IdGen,
): Category {
  const roles = template.roles.map((name) => ({ id: newId(), name }));
  return {
    id: newId(),
    name: template.name,
    roles,
    kinks: template.kinks.map((name) => ({
      id: newId(),
      name,
      ratings: Object.fromEntries(roles.map((role) => [role.id, unsetId])),
    })),
  };
}

export function createDefaultDoc(newId: IdGen, now: Date): KinklistDoc {
  const ratings = createDefaultRatings(newId);
  const unsetId = unsetRating(ratings).id;
  return {
    v: 3,
    title: '',
    ratings,
    categories: DEFAULT_CATEGORIES.map((template) => createCategory(template, unsetId, newId)),
    updatedAt: now.toISOString(),
  };
}
